import cashmemoService from '../../../services/cashmemoService';
import filterService from '../../../services/filterService';

import {
    setCashMemoLoading,
    setCashMemoError,
    setCashMemoList,
    setCashMemoCurrent,
    setCashMemoPagination,
    setCashMemoSort,
    setCashMemoFilters,
    setCashMemoDefaultFilter,
    setCashMemoSettings,
    clearCashMemoCurrent,
    clearCashMemoError,
    setCashMemoFilterLoading,
    changeCashMemoFilterCondition,
    addCashMemoFilterCondition,
    updateCashMemoFilterConditionRule,
    updateCashMemoFilterConditionValue,
    removeCashMemoFilterCondition,
    setCashMemoSelected,
    resetCashMemoFiltersAndPagination
} from './cashmemoSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().cashmemo.isSettingsInitialized) {
            dispatch(setCashMemoLoading(true));
            const response = await cashmemoService.getSettings();

            // Set the settings
            dispatch(setCashMemoSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setCashMemoDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setCashMemoError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setCashMemoLoading(false));
    }
};

export const fetchCashmemos = (params = {}, filter = {}, shouldUseFilter) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().cashmemo;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : {};

        dispatch(setCashMemoLoading(true));
        const response = await cashmemoService.getList(finalQueryParams, finalActiveFilter);
        dispatch(setCashMemoList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchCashmemos:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setCashMemoError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCashMemoLoading(false));
    }
};

export const fetchCashMemo = (id) => async (dispatch) => {
    try {
        dispatch(setCashMemoLoading(true));
        const response = await cashmemoService.getOne(id);
        dispatch(setCashMemoCurrent(response.data));
    } catch (error) {
        dispatch(setCashMemoError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setCashMemoLoading(false));
    }
};

export const createCashMemo = (data) => async (dispatch) => {
    try {
        const response = await cashmemoService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateCashMemo = (id, data) => async (dispatch) => {
    try {
        const response = await cashmemoService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteCashMemo = (id) => async (dispatch) => {
    try {
        dispatch(setCashMemoLoading(true));
        const response = await cashmemoService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setCashMemoError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setCashMemoLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setCashMemoPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setCashMemoSort(sort));
};

export const searchCashmemos = (query) => (dispatch, getState) => {
    const currentFilters = getState().cashmemo.filters;
    dispatch(setCashMemoFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setCashMemoDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeCashMemoFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addCashMemoFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateCashMemoFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateCashMemoFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeCashMemoFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setCashMemoFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().cashmemo.settings;
            dispatch(setCashMemoSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setCashMemoFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setCashMemoFilterLoading(false));
        dispatch(setCashMemoError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setCashMemoSelected(selectedRows));
    };
};

export const clearCurrentCashMemo = () => {
    return (dispatch) => {
        dispatch(clearCashMemoCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetCashMemoFiltersAndPagination());
};